// ─────────────────────────────────────────────────────────────────────────────
// V2Toast — the v2 version of the app-wide alert (components/Alert.js).
// Same contract: App passes `alert = { msg, type }` (type: success | danger | warning | info)
// via showAlert(); each new alert object becomes a toast. Glass banner, top-centre,
// stacks up to 3, pauses while hovered/focused, click or swipe up to dismiss.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Glyph } from "./icons";

const TONE = {
  success: { glyph: "check", cls: "good", label: "Done" },
  danger: { glyph: "alert", cls: "bad", label: "Error" },
  warning: { glyph: "alert", cls: "warn", label: "Warning" },
  info: { glyph: "sparkle", cls: "info", label: "Info" },
};
const LIFE = { success: 2600, info: 3000, warning: 4200, danger: 5200 }; // errors stay longer to be read
let seq = 0;

function Toast({ t, onDone }) {
  const [state, setState] = useState("in");
  const timer = useRef(0);
  const left = useRef(LIFE[t.type] || 3000);
  const started = useRef(0);
  const drag = useRef(null);
  const el = useRef(null);
  const tone = TONE[t.type] || TONE.info;

  const close = useCallback(() => { clearTimeout(timer.current); setState("out"); setTimeout(() => onDone(t.id), 280); }, [onDone, t.id]);
  const start = useCallback(() => { started.current = Date.now(); timer.current = setTimeout(close, left.current); }, [close]);
  const pause = () => { clearTimeout(timer.current); left.current = Math.max(800, left.current - (Date.now() - started.current)); };
  useEffect(() => { start(); return () => clearTimeout(timer.current); }, [start]);

  // Swipe up (touch / pointer) to dismiss
  const onDown = (e) => { drag.current = { y: e.clientY }; pause(); el.current?.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (!drag.current) return; const dy = Math.min(0, e.clientY - drag.current.y); el.current.style.transform = `translateY(${dy}px)`; el.current.style.opacity = String(1 + dy / 120); };
  const onUp = (e) => {
    if (!drag.current) return;
    const dy = e.clientY - drag.current.y; drag.current = null;
    if (dy < -36) { close(); return; }
    el.current.style.transform = ""; el.current.style.opacity = ""; start();
  };

  return (
    <div ref={el} className={`v2-toast v2-toast--${tone.cls}`} data-state={state} role={t.type === "danger" ? "alert" : "status"}
      onMouseEnter={pause} onMouseLeave={start} onFocus={pause} onBlur={start}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
      <span className="v2-toast-ico" aria-hidden="true"><Glyph name={tone.glyph} size={15} stroke={2.4} /></span>
      <span className="v2-toast-msg"><span className="v2-sr">{tone.label}: </span>{t.msg}</span>
      <button type="button" className="v2-toast-x" aria-label="Dismiss" onClick={close} onPointerDown={(e) => e.stopPropagation()}><Glyph name="close" size={13} stroke={2.2} /></button>
    </div>
  );
}

export default function V2Toast({ alert }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!alert?.msg) return;
    const id = ++seq;
    setList((l) => [...l.filter((x) => x.msg !== alert.msg || x.type !== (alert.type || "info")), { id, msg: alert.msg, type: alert.type || "info" }].slice(-3));
  }, [alert]);
  const remove = useCallback((id) => setList((l) => l.filter((x) => x.id !== id)), []);
  if (!list.length) return null;
  return createPortal(
    <div className="v2-layer v2-toasts" aria-live="polite">
      {list.map((t) => <Toast key={t.id} t={t} onDone={remove} />)}
    </div>, document.body);
}
